// src/pages/athlete/SWOT.js
import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "../../supabaseClient";
import { isAthleticPriorityEntry } from "../../lib/swotPriorityRouter";
import {
  Trophy,
  AlertCircle,
  Zap,
  ShieldAlert,
  Info,
  Layout,
  Lock
} from "lucide-react";

// --- 1. CONFIGURATION ---
const CATEGORY_CONFIG = {
  strengths: {
    label: "Strengths",
    singular: "Strength",
    icon: Trophy,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    border: "border-emerald-100",
    description: "Your competitive advantages."
  },
  weaknesses: {
    label: "Weaknesses",
    singular: "Weakness",
    icon: AlertCircle,
    color: "text-rose-600",
    bg: "bg-rose-50",
    border: "border-rose-100",
    description: "Areas for improvement."
  },
  opportunities: {
    label: "Opportunities",
    singular: "Opportunity",
    icon: Zap,
    color: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-100",
    description: "External factors to exploit."
  },
  threats: {
    label: "Threats",
    singular: "Threat",
    icon: ShieldAlert,
    color: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-100",
    description: "External challenges to manage."
  }
};

const CATEGORY_ORDER = ['strengths', 'weaknesses', 'opportunities', 'threats'];

const splitByAthleticBucket = (details) => {
  const skill = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
  const athletic = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
  CATEGORY_ORDER.forEach((categoryKey) => {
    (details?.[categoryKey] || []).forEach((item) => {
      const bucket = isAthleticPriorityEntry({ ...item, category: categoryKey, categoryKey }) ? athletic : skill;
      bucket[categoryKey].push(item);
    });
  });
  return { skill, athletic };
};

const mergeSwotDetails = (baseDetails, athleticDetails) => {
  const merged = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
  CATEGORY_ORDER.forEach((categoryKey) => {
    const combined = [...(baseDetails?.[categoryKey] || []), ...(athleticDetails?.[categoryKey] || [])];
    const seen = new Set();
    merged[categoryKey] = combined.filter((entry) => {
      const key = String(entry?.id || `${categoryKey}:${entry?.item || ""}:${entry?.section || ""}`).trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  });
  return merged;
};

const normalizeAction = (action) => {
  if (typeof action === "string") return { text: action.trim(), completed: false };
  if (action && typeof action === "object") {
    const tasks = Array.isArray(action.tasks)
      ? action.tasks.map((task) => ({
        text: String(task?.text || task?.item || "").trim(),
        is_completed: Boolean(task?.is_completed ?? task?.completed),
      })).filter((task) => task.text !== "")
      : [];
    const legacyText = String(action.text || action.item || action.action || "").trim();
    const normalizedTasks = tasks.length > 0
      ? tasks
      : (legacyText ? [{ text: legacyText, is_completed: Boolean(action.completed) }] : []);
    const isCompleted = normalizedTasks.length > 0
      ? normalizedTasks.every((task) => task.is_completed)
      : Boolean(action.is_completed ?? action.completed);
    return {
      text: legacyText,
      completed: isCompleted,
      microPointId: String(
        action.microPointId ||
        action.micro_point_id ||
        action.development_item_micro_point_id ||
        ""
      ).trim(),
      microPointTitle: String(
        action.microPointTitle ||
        action.micro_point_title ||
        action.microPoint ||
        ""
      ).trim(),
      tasks: normalizedTasks,
    };
  }
  return { text: "", completed: false, microPointId: "", microPointTitle: "", tasks: [] };
};

const normalizeSwotItem = (item) => {
  const actions = Array.isArray(item?.actions)
    ? item.actions.map(normalizeAction).filter((a) =>
      a.text !== "" || a.microPointId !== "" || a.microPointTitle !== ""
    )
    : [];
  const completedByActions = actions.length > 0 && actions.every((a) => a.completed);
  return {
    ...item,
    item: String(item?.item || "").trim(),
    section: String(item?.section || "General").trim(),
    actions,
    completed: Boolean(item?.completed) || completedByActions,
    priority: Boolean(item?.priority),
    priorityRank: Number.isFinite(item?.priorityRank) ? Number(item.priorityRank) : null
  };
};

// --- 2. SUB-COMPONENT: SWOT ITEM CARD ---
const SwotItemCard = ({ item, categoryKey }) => {
  const config = CATEGORY_CONFIG[categoryKey];
  const itemCompleted = Boolean(item.completed);

  return (
    <div
      className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm relative overflow-hidden"
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${config.color.replace('text', 'bg')}`}></div>

      <div className="pl-2">
        <h4 className={`font-bold leading-tight ${itemCompleted ? "text-gray-400 line-through" : "text-gray-900"}`}>
          {item.item}
        </h4>
        <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
          {item.section || "General"}
        </span>
      </div>
    </div>
  );
};

const SeasonPriorityCard = ({ item, categoryKey, priorityIndex, isExpanded, onToggleExpand, showChevron = true }) => {
  const config = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG.strengths;
  const actions = Array.isArray(item.actions) ? item.actions : [];
  const enteredActions = actions.filter((action) => {
    const tasks = Array.isArray(action?.tasks) ? action.tasks : [];
    return (
      String(action?.text || "").trim() !== "" ||
      String(action?.microPointId || "").trim() !== "" ||
      String(action?.microPointTitle || "").trim() !== "" ||
      tasks.some((task) => String(task?.text || "").trim() !== "")
    );
  });
  const allDevelopmentItemsComplete = enteredActions.length > 0 && enteredActions.every((action) => Boolean(action.completed));
  const completedSteps = enteredActions.reduce((count, action) => {
    const tasks = Array.isArray(action?.tasks) && action.tasks.length > 0
      ? action.tasks
      : [{ text: action?.text || "", is_completed: Boolean(action?.completed) }];
    return count + tasks.filter((task) => task?.text && task?.is_completed).length;
  }, 0);
  const totalSteps = 9;
  const progressLabel = `${completedSteps}/${totalSteps}`;

  return (
    <div className={`bg-white border rounded-2xl shadow-sm ${config.border}`}>
      <button
        type="button"
        onClick={onToggleExpand}
        className={`w-full text-left p-4 sm:p-5 ${onToggleExpand ? "cursor-pointer" : "cursor-default"}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className={`text-[10px] font-bold uppercase tracking-wider ${config.color}`}>{config.singular}</span>
            <p className={`mt-1 text-base sm:text-lg font-extrabold leading-tight ${allDevelopmentItemsComplete ? "text-gray-400 line-through" : "text-gray-900"}`}>
              {item.item}
            </p>
            <p className={`text-xs mt-1 ${allDevelopmentItemsComplete ? "text-gray-400 line-through" : "text-gray-500"}`}>
              {item.section || "General"}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="inline-flex items-center text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-full bg-blue-100 text-blue-700">
              Priority #{priorityIndex}
            </span>
            <span className="text-[10px] font-bold text-gray-500 bg-gray-100 border border-gray-200 px-2 py-1 rounded-full">
              {progressLabel}
            </span>
            {showChevron ? <span className="text-gray-400 text-sm">{isExpanded ? "▾" : "▸"}</span> : null}
          </div>
        </div>
      </button>

      {isExpanded ? (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-3">
          {[0, 1, 2].map((idx) => {
            const action = actions[idx];
            const hasAction = action?.text || action?.microPointTitle || (Array.isArray(action?.tasks) && action.tasks.length > 0);
            const tasks = Array.isArray(action?.tasks) && action.tasks.length > 0
              ? action.tasks
              : [{ text: action?.text || "", is_completed: Boolean(action?.completed) }];
            const filledTasks = tasks.filter((task) => String(task?.text || "").trim() !== "").length;
            return (
              <div key={`${item.id}-action-${idx}`} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                    {action?.microPointTitle || `Micro Point ${idx + 1}`}
                  </p>
                  <span className="text-[10px] font-bold text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
                    {filledTasks}/3
                  </span>
                </div>
                {!hasAction ? (
                  <p className="mt-2 text-xs text-gray-400 italic">Coach hasn't filled this micro point yet.</p>
                ) : (
                  <div className="mt-2 space-y-1.5">
                    {[0, 1, 2].map((taskIdx) => {
                      const task = tasks[taskIdx];
                      if (!task?.text) {
                        return <div key={`${item.id}-${idx}-task-${taskIdx}`} className="text-xs text-gray-400 italic">Step {taskIdx + 1}: Coach hasn't filled this yet.</div>;
                      }
                      return (
                        <label key={`${item.id}-${idx}-task-${taskIdx}`} className={`flex items-center gap-2 text-xs ${task.is_completed ? "text-gray-400 line-through" : "text-gray-600"}`}>
                          <input type="checkbox" checked={Boolean(task.is_completed)} readOnly />
                          <span>{task.text}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

// --- 4. MAIN PAGE COMPONENT ---
export default function AthleteSwotPage({ userId }) {
  const [activePrograms, setActivePrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [swotData, setSwotData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState("");
  const [selectedYear, setSelectedYear] = useState("all");
  const [showOnlyActivePriorities, setShowOnlyActivePriorities] = useState(false);
  const [selectedPriorityId, setSelectedPriorityId] = useState(null);
  const [priorityBucketTab, setPriorityBucketTab] = useState("skill");

  const parseSwotField = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.map(normalizeSwotItem).filter((item) => item.item !== "");
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(normalizeSwotItem).filter((item) => item.item !== "") : [];
    } catch (err) {
      return [];
    }
  };

  useEffect(() => {
    async function fetchUserEmail() {
      if (!userId) {
        setUserEmail("");
        return;
      }
      const { data, error } = await supabase
        .from("users")
        .select("email")
        .eq("id", userId)
        .maybeSingle();
      if (error) {
        console.error("Error fetching user email:", error);
        setUserEmail("");
        return;
      }
      setUserEmail(data?.email || "");
    }

    fetchUserEmail();
  }, [userId, userEmail]);

  const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

  useEffect(() => {
    async function fetchActiveSwots() {
      if (!userId) return;
      setLoading(true);
      try {
        const normalizedEmail = normalizeEmail(userEmail);
        let query = supabase
          .from("athlete_swot")
          .select(`
            id,
            program_id,
            position_id,
            strengths,
            weaknesses,
            opportunities,
            threats,
            updated_at,
            prebuilt_email,
            programs:programs (
              name,
              sport
            ),
            position:positions!athlete_swot_position_id_fkey (
              name
            )
          `)
          .in("status", ["draft", "published"])
          .order("updated_at", { ascending: false });

        if (normalizedEmail) {
          query = query.or(`athlete_id.eq.${userId},prebuilt_email.ilike.${normalizedEmail}`);
        } else {
          query = query.eq("athlete_id", userId);
        }

        const { data, error } = await query;

        if (error) throw error;

        if (data && data.length > 0) {
          const rawFormatted = data.map(item => ({
            swotId: item.id,
            programId: item.program_id,
            positionId: item.position_id || null,
            positionName: item.position?.name || null,
            programName: item.programs?.name || "Unknown Program",
            sport: item.programs?.sport || "General",
            updatedAt: item.updated_at,
            details: {
              strengths: parseSwotField(item.strengths),
              weaknesses: parseSwotField(item.weaknesses),
              opportunities: parseSwotField(item.opportunities),
              threats: parseSwotField(item.threats),
              updated_at: item.updated_at
            }
          }));
          const sharedAthleticByProgram = {};
          rawFormatted
            .filter((entry) => !entry.positionId)
            .forEach((entry) => {
              const programKey = String(entry.programId || "");
              if (!programKey || sharedAthleticByProgram[programKey]) return;
              sharedAthleticByProgram[programKey] = splitByAthleticBucket(entry.details).athletic;
            });

          const mergedFormatted = rawFormatted.map((entry) => {
            if (!entry.positionId) return entry;
            const sharedAthletic = sharedAthleticByProgram[String(entry.programId || "")];
            if (!sharedAthletic) return entry;
            const skillOnly = splitByAthleticBucket(entry.details).skill;
            return {
              ...entry,
              details: {
                ...mergeSwotDetails(skillOnly, sharedAthletic),
                updated_at: entry.details?.updated_at || entry.updatedAt
              }
            };
          });
          const positionedProgramIds = new Set(
            mergedFormatted
              .filter((entry) => Boolean(entry.positionId))
              .map((entry) => String(entry.programId))
          );
          const formatted = mergedFormatted.filter((entry) => {
            if (entry.positionId) return true;
            return !positionedProgramIds.has(String(entry.programId));
          });

          setActivePrograms(formatted);
          setSelectedProgram(formatted[0]);
          setSwotData(formatted[0].details);
        }
      } catch (err) {
        console.error("Error fetching active SWOTs:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchActiveSwots();
  }, [userId]);

  const handleProgramSelect = (prog) => {
    setSelectedProgram(prog);
    setSwotData(prog.details);
  };

  const availableYears = useMemo(() => {
    const years = new Set(
      (activePrograms || [])
        .map((prog) => {
          const date = new Date(prog?.updatedAt || "");
          return Number.isNaN(date.getTime()) ? null : String(date.getFullYear());
        })
        .filter(Boolean)
    );
    return Array.from(years).sort((a, b) => Number(b) - Number(a));
  }, [activePrograms]);

  const filteredPrograms = useMemo(() => {
    if (selectedYear === "all") return activePrograms;
    return (activePrograms || []).filter((prog) => {
      const date = new Date(prog?.updatedAt || "");
      return !Number.isNaN(date.getTime()) && String(date.getFullYear()) === selectedYear;
    });
  }, [activePrograms, selectedYear]);

  useEffect(() => {
    if (!selectedProgram) return;
    const stillValid = filteredPrograms.some((prog) => String(prog.swotId) === String(selectedProgram.swotId));
    if (stillValid) return;
    const next = filteredPrograms[0] || null;
    setSelectedProgram(next);
    setSwotData(next?.details || null);
  }, [filteredPrograms, selectedProgram]);

  const seasonPrioritiesAll = useMemo(() => (
    selectedProgram && swotData
      ? CATEGORY_ORDER
        .flatMap((categoryKey) =>
          (swotData[categoryKey] || [])
            .filter((item) => item.priority)
            .map((item) => ({ ...item, categoryKey }))
        )
        .sort((a, b) => (a.priorityRank || 999) - (b.priorityRank || 999))
      : []
  ), [selectedProgram, swotData]);
  const seasonPriorities = useMemo(() => (
    showOnlyActivePriorities
      ? seasonPrioritiesAll.filter((item) => !item.completed)
      : seasonPrioritiesAll
  ), [seasonPrioritiesAll, showOnlyActivePriorities]);
  const groupedSeasonPriorities = useMemo(() => (
    seasonPriorities.reduce((acc, item) => {
      const bucket = isAthleticPriorityEntry(item) ? "athletic" : "skill";
      acc[bucket].push(item);
      return acc;
    }, { skill: [], athletic: [] })
  ), [seasonPriorities]);
  const visibleSeasonPriorities = useMemo(
    () => groupedSeasonPriorities[priorityBucketTab] || [],
    [groupedSeasonPriorities, priorityBucketTab]
  );
  const visiblePriorityIdsKey = useMemo(
    () => visibleSeasonPriorities.map((item) => item.id).join("|"),
    [visibleSeasonPriorities]
  );

  useEffect(() => {
    const exists = visibleSeasonPriorities.some((item) => item.id === selectedPriorityId);
    if (!exists) setSelectedPriorityId(null);
  }, [selectedProgram?.programId, showOnlyActivePriorities, priorityBucketTab, visiblePriorityIdsKey, visibleSeasonPriorities, selectedPriorityId]);

  const prioritySummary = [0, 1, 2].map((idx) => {
    const item = visibleSeasonPriorities[idx];
    if (!item) return { label: `Priority ${idx + 1}`, complete: false, hasValue: false };
    return { id: item.id, label: item.item, complete: Boolean(item.completed), hasValue: true };
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-20 font-sans">
      <div className="bg-white px-4 sm:px-6 py-4 shadow-sm sticky top-0 z-10 border-b border-gray-100">
        <h1 className="text-lg sm:text-xl font-extrabold text-gray-900 text-center tracking-tight">
          Performance Analysis
        </h1>
      </div>

      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 space-y-5 sm:space-y-6">
        {filteredPrograms.length > 0 ? (
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">
                Evaluated By
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Year</span>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="all">All</option>
                  {availableYears.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {filteredPrograms.map((prog) => {
                const isActive = String(selectedProgram?.swotId || "") === String(prog.swotId || "");
                return (
                  <button
                    key={prog.swotId}
                    onClick={() => handleProgramSelect(prog)}
                    className={`
                      whitespace-nowrap px-4 py-2 rounded-xl text-sm font-bold transition-all border
                      ${isActive
                        ? "bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-200"
                        : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"
                      }
                    `}
                  >
                    {prog.programName}{prog.positionName ? ` • ${prog.positionName}` : ""}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-3xl border border-gray-200 border-dashed text-center px-6">
            <div className="bg-gray-50 p-4 rounded-full mb-4">
              <Layout size={32} className="text-gray-300" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">No Analysis Available</h3>
            <p className="text-gray-500 text-sm mt-1 max-w-xs">
              When a coach creates a SWOT analysis for you, it will appear here.
            </p>
          </div>
        )}

        {selectedProgram && swotData && (
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-lg font-extrabold text-gray-900">Season Priorities</h2>
              <p className="text-sm text-gray-500 mt-1">Choose 1-3 priorities to focus on this season</p>
              <div className="mt-3 inline-flex items-center rounded-xl border border-gray-200 bg-gray-50 p-1">
                <button
                  type="button"
                  onClick={() => setPriorityBucketTab("skill")}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition ${priorityBucketTab === "skill" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:text-gray-800"}`}
                >
                  Skill ({groupedSeasonPriorities.skill.length})
                </button>
                <button
                  type="button"
                  onClick={() => setPriorityBucketTab("athletic")}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition ${priorityBucketTab === "athletic" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:text-gray-800"}`}
                >
                  Athletic ({groupedSeasonPriorities.athletic.length})
                </button>
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {prioritySummary.map((entry, idx) => (
                  <button
                    type="button"
                    key={`priority-summary-${idx}`}
                    onClick={() => {
                      if (!entry.id) return;
                      setSelectedPriorityId((prev) => (prev === entry.id ? null : entry.id));
                    }}
                    disabled={!entry.hasValue}
                    className={`rounded-lg border px-3 py-2 text-left transition ${
                      !entry.hasValue
                        ? "bg-gray-50/50 border-gray-100 cursor-not-allowed"
                        : selectedPriorityId === entry.id
                          ? "bg-blue-50 border-blue-300"
                          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Priority {idx + 1}</p>
                    <p className={`text-xs font-semibold mt-1 truncate ${entry.hasValue ? (entry.complete ? "text-gray-400 line-through" : "text-gray-700") : "text-gray-400 italic"}`}>
                      {entry.hasValue ? entry.label : "Not set yet"}
                    </p>
                  </button>
                ))}
              </div>
            </div>
            <div className="p-4">
              {visibleSeasonPriorities.length === 0 ? (
                <p className="text-sm text-gray-400 italic">
                  No {priorityBucketTab === "athletic" ? "athletic" : "skill"} priorities selected yet.
                </p>
              ) : (
                <div className="mt-1">
                  {selectedPriorityId ? (() => {
                    const selected = visibleSeasonPriorities.find((item) => item.id === selectedPriorityId);
                    if (!selected) return null;
                    const selectedIndex = visibleSeasonPriorities.findIndex((item) => item.id === selected.id);
                    return (
                      <SeasonPriorityCard
                        key={selected.id}
                        item={selected}
                        categoryKey={selected.categoryKey}
                        priorityIndex={selectedIndex + 1}
                        isExpanded={true}
                        onToggleExpand={null}
                        showChevron={false}
                      />
                    );
                  })() : (
                    <p className="text-xs text-gray-400 italic px-1 py-2">Select a priority tab above to view details.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {selectedProgram && swotData && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-500">
            {['strengths', 'weaknesses', 'opportunities', 'threats'].map((key) => {
              const items = swotData[key] || [];
              const config = CATEGORY_CONFIG[key];
              const Icon = config.icon;

              return (
                <div key={key} className="flex flex-col h-full">
                  <div className="flex items-center justify-between mb-3 px-1">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded-md ${config.bg}`}>
                        <Icon size={16} className={config.color} />
                      </div>
                      <h3 className="text-sm font-extrabold text-gray-800 uppercase tracking-wide">
                        {config.label}
                      </h3>
                    </div>
                    <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                      {items.length}
                    </span>
                  </div>

                  <div className="space-y-3 flex-1">
                    {items.length === 0 ? (
                      <div className="bg-gray-50/50 border border-gray-100 rounded-xl p-6 text-center h-full min-h-[100px] flex items-center justify-center">
                        <p className="text-gray-400 text-xs font-medium italic">No {key} noted.</p>
                      </div>
                    ) : (
                      items.map((item) => (
                        <SwotItemCard
                          key={item.id}
                          item={item}
                          categoryKey={key}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {selectedProgram && (
          <div className="flex items-center justify-center gap-2 mt-8 opacity-50">
            <Info size={14} className="text-gray-400" />
            <p className="text-xs font-medium text-gray-400">
              Sport: {selectedProgram.sport} • Updated: {new Date(selectedProgram.updatedAt).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>

    </div>
  );
}
